// Pure listing helpers — no React.

function canonicalUrl(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    const host = u.hostname.toLowerCase().replace(/^www\./, "");
    const path = u.pathname.replace(/\/$/, "");
    return `${host}${path}`.toLowerCase();
  } catch {
    return trimmed
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/[?#].*$/, "")
      .replace(/\/$/, "");
  }
}

function upsertListing(listings, { url = "", title, description, amount, isYou = false }, now = Date.now(), nextId) {
  const canonical = canonicalUrl(url);
  const idx = listings.findIndex((l) => (l.canonical ?? canonicalUrl(l.url)) === canonical);
  if (idx >= 0) {
    return listings.map((l, i) =>
      i === idx ? { ...l, amount: l.amount + amount, url: url || l.url, updated_at: now } : l
    );
  }
  const entry = {
    id: nextId !== undefined ? nextId : Math.max(0, ...listings.map((l) => Number(l.id) || 0)) + 1,
    url: url || "",
    title,
    description:
      description || `${title} just joined the rescue effort with an opening contribution of fuel.`,
    favicon_url: faviconFor(url || "https://example.com"),
    amount,
    clicks: 0,
    isYou,
    created_at: now,
    updated_at: now,
  };
  if (canonical) entry.canonical = canonical;
  return [...listings, entry];
}

function sortListings(list) {
  return [...list].sort((a, b) => b.amount - a.amount);
}

function listingProgress(listings, goal = 10000) {
  const sum = listings.reduce((acc, l) => acc + (Number(l.amount) || 0), 0);
  return Math.min(sum / goal, 1);
}

function faviconFor(url) {
  try {
    const host = new URL(url.startsWith("http") ? url : `https://${url}`).hostname;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
  } catch {
    return null;
  }
}

// Optimistic click bump: +1 for the matching listing, immutably.
function bumpClicks(list, id) {
  return list.map((l) => (l.id === id ? { ...l, clicks: (Number(l.clicks) || 0) + 1 } : l));
}

// Bare domain for display (firstbid.lol style): "https://www.prompts.chat/x" -> "prompts.chat".
function bareDomain(url) {
  try {
    const host = new URL(url.startsWith("http") ? url : `https://${url}`).hostname;
    return host.replace(/^www\./, "");
  } catch {
    return url || "";
  }
}

const GENERIC_SOCIAL_TITLES = new Set([
  "instagram",
  "instagram.com",
  "www.instagram.com",
  "x",
  "x.com",
  "twitter",
  "twitter.com",
  "www.twitter.com",
  "tiktok",
  "tiktok.com",
  "www.tiktok.com",
  "youtube",
  "youtube.com",
  "www.youtube.com",
]);

function parseSocialProfile(rawUrl) {
  if (!rawUrl) return null;
  let u;
  try {
    u = new URL(String(rawUrl).trim());
  } catch {
    return null;
  }
  const host = u.hostname.toLowerCase().replace(/^www\./, "");
  const parts = u.pathname.split("/").filter(Boolean);

  if (host === "instagram.com" || host.endsWith(".instagram.com")) {
    if (!parts.length) return null;
    const head = parts[0].toLowerCase();
    if (["p", "reel", "reels", "stories", "explore", "accounts", "direct", "tv"].includes(head)) return null;
    const handle = parts[0].replace(/^@/, "");
    if (!handle) return null;
    return { platform: "instagram", handle };
  }
  if (host === "x.com" || host === "twitter.com" || host.endsWith(".twitter.com")) {
    if (!parts.length) return null;
    const head = parts[0].toLowerCase();
    if (["i", "intent", "home", "search", "hashtag", "share"].includes(head)) return null;
    const handle = parts[0].replace(/^@/, "");
    if (!handle) return null;
    return { platform: "x", handle };
  }
  if (host === "tiktok.com" || host.endsWith(".tiktok.com")) {
    if (!parts.length) return null;
    if (parts[0].startsWith("@")) return { platform: "tiktok", handle: parts[0].slice(1) };
    return null;
  }
  if (host === "youtube.com" || host.endsWith(".youtube.com")) {
    const at = parts.find((p) => p.startsWith("@"));
    if (at) return { platform: "youtube", handle: at.slice(1) };
    return null;
  }
  return null;
}

function isGenericSocialTitle(title, platform) {
  if (!title) return true;
  const t = String(title).trim().toLowerCase();
  if (!t) return true;
  if (GENERIC_SOCIAL_TITLES.has(t)) return true;
  if (/^(www\.)?[a-z0-9.-]+\.[a-z]{2,}$/i.test(t) && t.includes(".")) return true;
  return false;
}

function isSyntheticSocialTitle(title, rawUrl) {
  const profile = parseSocialProfile(rawUrl);
  if (!profile || !title) return false;
  return String(title).trim() === `@${profile.handle}`;
}

function isSyntheticSocialDescription(description, rawUrl) {
  const profile = parseSocialProfile(rawUrl);
  if (!profile || !description) return false;
  const handle = `@${profile.handle}`;
  const label =
    profile.platform === "instagram"
      ? "Instagram"
      : profile.platform === "x"
        ? "X"
        : profile.platform === "tiktok"
          ? "TikTok"
          : profile.platform === "youtube"
            ? "YouTube"
            : "Social";
  const d = String(description).trim();
  return d === `${label} profile ${handle}` || d === `Instagram profile ${handle}`;
}

function needsSocialMetaUpgrade(listing) {
  if (!listing || !listing.url) return false;
  const profile = parseSocialProfile(listing.url);
  if (!profile) return false;
  return (
    isGenericSocialTitle(listing.title, profile.platform) ||
    isSyntheticSocialTitle(listing.title, listing.url) ||
    !listing.description ||
    isSyntheticSocialDescription(listing.description, listing.url)
  );
}

/** Board label: social profiles show @handle (or scraped title); websites show bare domain. */
function displayName(listing) {
  const url = listing?.url || "";
  const title = (listing?.title || "").trim();
  const profile = parseSocialProfile(url);
  if (profile) {
    if (title && !isGenericSocialTitle(title, profile.platform)) return title;
    return `@${profile.handle}`;
  }
  return bareDomain(url) || title || "listing";
}

function previewLabels(url, meta = {}) {
  const profile = parseSocialProfile(url);
  const title = (meta.title || "").trim();
  const description = (meta.description || "").trim();
  const loginWall =
    /create an account or log in to instagram|sign up to see|login to view photos from people you follow/i.test(
      description
    );
  const desc = loginWall ? "" : description;

  if (profile) {
    const name =
      title && !isGenericSocialTitle(title, profile.platform) ? title : `@${profile.handle}`;
    const subtitle =
      title && !isGenericSocialTitle(title, profile.platform) && title !== name ? `@${profile.handle}` : "";
    return { name, subtitle, description: desc };
  }

  const domain = bareDomain(url);
  const subtitle = title && title !== domain ? title : "";
  return { name: domain || url, subtitle, description: desc };
}

export {
  canonicalUrl,
  upsertListing,
  sortListings,
  listingProgress,
  faviconFor,
  bareDomain,
  bumpClicks,
  parseSocialProfile,
  displayName,
  previewLabels,
  isGenericSocialTitle,
  isSyntheticSocialTitle,
  isSyntheticSocialDescription,
  needsSocialMetaUpgrade,
};
